import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGE_CODES, uiText } from './uiStrings';
import type { Locale } from '@/types/api.types';

interface LanguageContextValue {
  lang: Locale;
  setLang: (code: Locale) => void;
  /** Resolve a static UI string (with optional {placeholder} params). */
  ui: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);
const STORAGE_KEY = 'dc.lang';

function detect(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED_LANGUAGE_CODES.includes(saved as Locale)) return saved as Locale;
  } catch {
    /* storage unavailable */
  }
  // First visit: always Spanish (primary audience), whatever the browser language is.
  // The selector stays available and the choice is remembered above.
  return DEFAULT_LANGUAGE;
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Locale>(detect);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((code: Locale) => {
    setLangState(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo<LanguageContextValue>(
    () => ({ lang, setLang, ui: (key, params) => uiText(lang, key, params) }),
    [lang, setLang],
  );
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within a LanguageProvider');
  return ctx;
}
